import { ViewModel } from "../mvvm/ViewModel.js";
/**
 * Per-listing staff edits: blank create, patch save, archive, and photo upload.
 * The server enforces roles; this VM carries no capability gate of its own.
 */
export class ListingEditorViewModel extends ViewModel {
    client;
    siteSlug;
    constructor(client, siteSlug) {
        super({ listings: [], loading: false, uploading: false, error: null });
        this.client = client;
        this.siteSlug = siteSlug;
    }
    async load() {
        const rows = await this.run(async () => {
            const listings = await this.client.listListings(this.siteSlug);
            this.setState({ listings });
            return listings;
        }, "Failed to load inventory.");
        return rows ?? [];
    }
    /** New unpublished-style placeholder the staff then edits. */
    async createBlank() {
        const created = await this.run(async () => {
            const row = await this.client.createListing(this.siteSlug, {
                make: "New",
                model: "Vehicle",
                year: new Date().getFullYear(),
                priceCents: 0,
                purchaseCents: 0,
                status: "available",
                mileage: 0,
                vin: "",
                description: "",
                imageKeys: [],
            });
            this.setState({ listings: [row, ...this.state.listings] });
            return row;
        }, "Failed to add car.");
        return created ?? null;
    }
    /** Merge `patch` over the current row (so unrelated fields like VIN survive) and save. */
    async save(id, patch) {
        const updated = await this.run(async () => {
            const base = this.state.listings.find((l) => l.id === id) ??
                (await this.client.getListing(this.siteSlug, id));
            const { id: _ignored, ...rest } = patch;
            const input = {
                make: base.make,
                model: base.model,
                year: base.year,
                priceCents: base.priceCents,
                purchaseCents: base.purchaseCents ?? 0,
                status: base.status,
                mileage: base.mileage,
                vin: base.vin,
                description: base.description,
                imageKeys: base.imageKeys,
                ...rest,
            };
            const row = await this.client.updateListing(this.siteSlug, id, input);
            this.replace(row);
            return row;
        }, "Failed to save listing.");
        return updated ?? null;
    }
    async archive(id) {
        const ok = await this.run(async () => {
            const row = await this.client.archiveListing(this.siteSlug, id);
            this.replace(row);
            return true;
        }, "Failed to delete listing.");
        return ok === true;
    }
    /** Uploads each photo, appends the keys to the listing, and saves. */
    async addPhotos(id, files) {
        const updated = await this.run(async () => {
            const base = this.state.listings.find((l) => l.id === id) ??
                (await this.client.getListing(this.siteSlug, id));
            const keys = [...base.imageKeys];
            for (const file of files) {
                const upload = await this.client.upload(file.blob, file.name, file.blob.type || "image/jpeg");
                keys.push(upload.key);
            }
            const row = await this.client.updateListing(this.siteSlug, id, {
                make: base.make,
                model: base.model,
                year: base.year,
                priceCents: base.priceCents,
                purchaseCents: base.purchaseCents ?? 0,
                status: base.status,
                mileage: base.mileage,
                vin: base.vin,
                description: base.description,
                imageKeys: keys,
            });
            this.replace(row);
            return row;
        }, "Photo upload failed.", { flag: "uploading" });
        return updated ?? null;
    }
    replace(row) {
        const has = this.state.listings.some((l) => l.id === row.id);
        this.setState({
            listings: has
                ? this.state.listings.map((l) => (l.id === row.id ? row : l))
                : [row, ...this.state.listings],
        });
    }
}
//# sourceMappingURL=ListingEditorViewModel.js.map